import React, { FC, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import classNames from 'classnames';
import { Form, Formik, FormikHelpers } from 'formik';

import type { PersonalDataFromFormik } from './types';

import { API } from 'app/api';
import { formSubmissionSuccessDelay } from 'app/constants/formConstants';
import { LoadingStatus } from 'app/constants/SideEffectStatus';
import { Modal } from 'app/design-system/components/Modal';
import { useTranslation } from 'app/lang';
import { addPersonalData } from 'app/lib/account/reducers/clientInfo/clientInfoReducer';
import { saveUserFullNameToStorage } from 'app/modules/BetSlip/utils/user-full-name-storage';
import { createGaEvent, dispatchGaEvent } from 'app/packages/analytics';

import ModalBody from './components/Body';
import ModalFooter from './components/Footer';
import { PERSONAL_DATA_MODAL_PARAM_KEY } from './constants';
import type { ErrorMessageKey } from './utils';
import {
  getPersonalDataErrorMessage,
  personalDataValidation,
  preparePersonalDataToBackend,
} from './utils';

import dictionary from './PersonalDataModal.dictionary.json';

export const PERSONAL_INITIAL_VALUES = {
  firstName: '',
  lastName: '',
  middleName: '',
};

type PersonalDataModalParams = {
  showModal?: boolean;
};

export const PersonalDataModal: FC<PersonalDataModalParams> = ({ showModal }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const validate = personalDataValidation(t);
  const { location, replace } = useHistory();
  const { search, pathname } = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const isOpen = typeof showModal !== 'undefined' ? showModal : urlParams.has(PERSONAL_DATA_MODAL_PARAM_KEY);
  const handleClose = () => replace({ search: '' });

  const handleSubmit = useCallback(
    async (
      values: PersonalDataFromFormik,
      actions: FormikHelpers<PersonalDataFromFormik>,
    ) => {
      const data = preparePersonalDataToBackend(values);

      API.client.clientProfile.savePersonalData(data)
        .then(() => {
          dispatchGaEvent(createGaEvent({ event: 'profileAddNameConfirm' }));
          actions.setStatus(LoadingStatus.SUCCESS);
          const fullName = classNames(
            data.lastName.trim(),
            data.firstName.trim(),
            data.middleName?.trim(),
          );

          const payload = {
            ...data,
            fullName,
          };

          if (
            search && search.includes('fromBetSlip')
            || pathname.includes('profile')
            || pathname.includes('withdrawal')
          ) {
            saveUserFullNameToStorage(payload);
          }

          setTimeout(() => {
            dispatch(addPersonalData(payload));
            handleClose();
          }, formSubmissionSuccessDelay);
        })
        .catch((err) => {
          const alert = getPersonalDataErrorMessage[err.response.status as ErrorMessageKey];
          actions.setStatus(err.response.status);
          actions.setErrors({ alert });
          console.error('PersonalDataModal error', err);
        });
    },
    [search, pathname],
  );

  return (
    <Modal isOpen={isOpen}
           closable
           onClose={handleClose}
           data-test="Modal-personalData">
      <Modal.Header>
        {t(dictionary.title)}
      </Modal.Header>
      <Formik validate={validate}
              onSubmit={handleSubmit}
              initialValues={PERSONAL_INITIAL_VALUES}>
        <Form noValidate>
          <ModalBody />
          <ModalFooter handleClose={handleClose} />
        </Form>
      </Formik>
    </Modal>
  );
};
